import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { LogIn, Mail, Lock, AlertCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await login(email, password);
    
    setLoading(false);
    
    if (result.success) {
      // Route by role: admin → admin dashboard, partner → partner dashboard, otherwise honour `from` or /resources
      const role = result.user?.role;
      let target = location.state?.from?.pathname;
      if (!target) {
        if (role === 'admin') target = '/admin/dashboard';
        else if (role === 'partner') target = '/partner/dashboard';
        else target = '/resources';
      }
      navigate(target, { replace: true });
    } else {
      setError(result.error);
    }
  };

  return (
    <div className="min-h-screen pt-20 flex items-center justify-center bg-ewss-navy">
      <div className="max-w-md w-full px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="bg-gradient-to-br from-black/40 to-ewss-navy/40 backdrop-blur-sm rounded-2xl p-8 border border-ewss-cyan/30"
        >
          {/* Header */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-ewss-cyan/10 rounded-xl flex items-center justify-center text-ewss-cyan mx-auto mb-4">
              <LogIn className="w-8 h-8" />
            </div>
            <h2 className="text-3xl font-bold text-white mb-2">Welcome Back</h2>
            <p className="text-gray-400">Sign in to access the Resource Vault</p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-lg flex items-start gap-3">
              <AlertCircle className="text-red-500 flex-shrink-0 mt-0.5" size={20} />
              <p className="text-red-400 text-sm">{error}</p>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
                Email Address
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className="text-gray-500" size={20} />
                </div>
                <input
                  id="email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="block w-full pl-10 pr-3 py-3 bg-black/30 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-ewss-cyan focus:border-transparent"
                  placeholder="you@example.com"
                  data-testid="login-email-input"
                />
              </div>
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-300 mb-2">
                Password
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="text-gray-500" size={20} />
                </div>
                <input
                  id="password"
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="block w-full pl-10 pr-3 py-3 bg-black/30 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-ewss-cyan focus:border-transparent"
                  placeholder="••••••••"
                  data-testid="login-password-input"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-ewss-cyan text-ewss-navy font-semibold rounded-lg hover:shadow-[0_0_30px_rgba(23,145,255,0.5)] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="login-submit-button"
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
          </form>

          {/* Forgot Password */}
          <div className="mt-4 text-center">
            <Link
              to="/forgot-password"
              className="text-gray-400 hover:text-ewss-cyan text-sm transition-colors"
              data-testid="login-forgot-password-link"
            >
              Forgot your password?
            </Link>
          </div>

          {/* Footer */}
          <div className="mt-6 text-center">
            <p className="text-gray-400 text-sm">
              Don't have an account?{' '}
              <Link to="/register" className="text-ewss-cyan hover:underline font-semibold">
                Register here
              </Link>
            </p>
          </div>

          {/* Admin Note */}
          <div className="mt-6 pt-6 border-t border-gray-700">
            <p className="text-gray-500 text-xs text-center">
              Admin access: Use your admin credentials to manage resources
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Login;
